'use client'
import { createContext, useContext, useState, useCallback, useEffect, useLayoutEffect, useRef, startTransition } from 'react'
import { usePathname, useRouter } from 'next/navigation'
import { createClient } from '@/utils/supabase/client'
import type { CommandFormState } from '@/components/automations/CommandFormPage'
import type { CommandFormSnapshot } from '@/lib/commands/form-snapshot'
// ChatMode keeps the union narrow at every call site (thread API, message
// loader, bookmarks).
export type ChatMode = 'normal' | 'god'

type FlowIntent = 'campaign' | 'video' | 'brand' | null

export interface BrandChatContext {
  field?: string
  currentValue?: string
  seedMessage?: string
  /** When true with seedMessage, the chat sends the seed as the first user message automatically. */
  autoSendSeed?: boolean
}

export interface PendingFieldContext {
  field: string
  currentValue: string
}

export interface PendingEventContext {
  id: string
  event_type: string
  title: string
  description?: string | null
  metadata?: Record<string, unknown>
}

export interface PendingSonobiQuery {
  query: string
  variables?: Record<string, unknown>
  autoExecute?: boolean
}

export interface PendingCommandFormContext {
  commandId: string | null
  pathname: string
  formState: CommandFormSnapshot
  fullFormState?: CommandFormState
  focusedField?: string
}

export type ProposedPatch = {
  id: string
  patch: Partial<CommandFormState>
  warnings: string[]
  errors?: string[]
  dangerousToolsAdded?: string[]
  summary: string
  reasoning: string
  createdAt: number
}

interface ChatContextValue {
  chatOpen: boolean
  flowIntent: FlowIntent
  seedMessage: string | null
  pendingFieldContext: PendingFieldContext | null
  setPendingFieldContext: (ctx: PendingFieldContext | null) => void
  /** Queue an additional field to the pending context (multi-quote). */
  addPendingFieldContext: (ctx: PendingFieldContext) => void
  /** All currently queued field contexts (for multi-quote banner). */
  pendingFieldContexts: PendingFieldContext[]
  setPendingFieldContexts: (ctxs: PendingFieldContext[]) => void
  pendingEventContext: PendingEventContext | null
  setPendingEventContext: (ctx: PendingEventContext | null) => void
  pendingSonobiQuery: PendingSonobiQuery | null
  setPendingSonobiQuery: (q: PendingSonobiQuery | null) => void
  pendingCommandFormContext: PendingCommandFormContext | null
  setPendingCommandFormContext: React.Dispatch<React.SetStateAction<PendingCommandFormContext | null>>
  proposedCommandFormPatches: ProposedPatch[]
  setProposedCommandFormPatches: React.Dispatch<React.SetStateAction<ProposedPatch[]>>
  openChat: (intent?: FlowIntent, context?: BrandChatContext) => void
  closeChat: () => void
  toggleChat: () => void
  clearSeedMessage: () => void
  /** Clears the “auto-send first message” flag (e.g. after it runs). */
  clearAutoSendSeed: () => void
  autoSendSeed: boolean
  currentThreadId: string | null
  setCurrentThreadId: (id: string | null) => void
  godMode: boolean
  setGodMode: (enabled: boolean) => void
  /** File dropped via the global drag-and-drop overlay. ChatInterface consumes and clears it. */
  droppedFile: File | null
  setDroppedFile: (file: File | null) => void
  /** Set by AppSidebar; called when chat opens so the app rail can collapse. */
  registerAppRailCollapser: (fn: (() => void) | null) => void
  /** `null` when AppSidebar is not mounted; overlay app counts as `expanded`. */
  appSidebarRailMode: 'expanded' | 'collapsed' | null
  registerAppSidebarRailMode: (mode: 'expanded' | 'collapsed' | null) => void
  /** Collapse the app sidebar rail; no-op if AppSidebar is not mounted (e.g. /chat) or in overlay mode. */
  collapseAppSidebar: () => void
  /** Set by AppSidebar; expands/collapses the app rail (no-op when not registered, e.g. overlay or unmounted). */
  registerAppRailToggler: (fn: (() => void) | null) => void
  toggleAppSidebar: () => void
  /** When set, ChatInterface scrolls to this message id after the panel opens (e.g. collapsed-rail bookmark). */
  pendingScrollToMessageId: string | null
  setPendingScrollToMessageId: (id: string | null) => void
  /** Campaign chat viewport fullscreen overlay: lobby hides in-flow app sidebar; use drawer + hamburger. */
  panelViewportFullscreen: boolean
  setPanelViewportFullscreen: (open: boolean) => void
  flagUrlChangeSource: (source: 'tool-navigation') => void
  isOnboarding: boolean
  completingOnboarding: boolean
  setCompletingOnboarding: (completing: boolean) => void
  /** App sidebar overlay drawer visible (only while panelViewportFullscreen). */
  appSidebarOverlayOpen: boolean
  setAppSidebarOverlayOpen: (open: boolean) => void
  toggleAppSidebarOverlay: () => void
}

const ChatContext = createContext<ChatContextValue | null>(null)

const LS_CHAT_WAS_OPEN = 'chatWasOpen'
const COOKIE_CHAT_WAS_OPEN = 'monarch.chatWasOpen'

const CHAT_DIRECT_EXIT_PATH = '/campaigns'

const SEED_MESSAGES: Record<string, string> = {
  campaign: 'I want to create a video ad campaign.',
  video: 'I want to create a standalone video.',
  brand: 'I want to update my brand settings.',
}

// Scope localStorage keys to the current user to prevent cross-user leakage
function getUserScopedKey(baseKey: string): string {
  try {
    const keys = Object.keys(localStorage)
    const authKey = keys.find(k => k.startsWith('sb-') && k.endsWith('-auth-token') && !k.includes('.'))
    if (!authKey) return baseKey
    const raw = localStorage.getItem(authKey)
    if (raw) {
      const parsed = JSON.parse(raw)
      const uid = parsed?.user?.id
      if (uid) return `${baseKey}:${uid}`
    }
  } catch { /* ignore */ }
  return baseKey
}

export function ChatProvider({
  children,
  initialChatWasOpen = false,
}: {
  children: React.ReactNode
  initialChatWasOpen?: boolean
}) {
  const router = useRouter()
  const pathname = usePathname()
  const isInitialChatRoute = pathname === '/chat'
  const [chatOpen, setChatOpen] = useState(isInitialChatRoute || initialChatWasOpen)
  const [flowIntent, setFlowIntent] = useState<FlowIntent>(null)
  const [seedMessage, setSeedMessage] = useState<string | null>(null)
  const [pendingFieldContext, setPendingFieldContext] = useState<PendingFieldContext | null>(null)
  const [pendingFieldContexts, setPendingFieldContexts] = useState<PendingFieldContext[]>([])
  const [pendingEventContext, setPendingEventContext] = useState<PendingEventContext | null>(null)
  const [currentThreadId, setCurrentThreadIdState] = useState<string | null>(null)
  const [godMode, setGodModeState] = useState(false)
  // Ref so callbacks can read the current mode without stale closures.
  const godModeRef = useRef(false)
  const [pendingSonobiQuery, setPendingSonobiQuery] = useState<PendingSonobiQuery | null>(null)
  const [pendingCommandFormContext, setPendingCommandFormContext] = useState<PendingCommandFormContext | null>(null)
  const [proposedCommandFormPatches, setProposedCommandFormPatches] = useState<ProposedPatch[]>([])
  const [autoSendSeed, setAutoSendSeed] = useState(false)
  const [droppedFile, setDroppedFile] = useState<File | null>(null)
  const [pendingScrollToMessageId, setPendingScrollToMessageId] = useState<string | null>(null)

  // Wrapped setter: when the mode flips, swap the active thread to the one
  // saved for the *new* mode (or null — ChatInterface will create one). This
  // is the lever that makes god mode "switch to a totally different
  // conversation" rather than mix histories. ChatInterface re-keys on mode so
  // it remounts cleanly with empty in-memory state.
  const setGodMode = useCallback((enabled: boolean) => {
    setGodModeState((prev) => {
      if (prev === enabled) return prev
      godModeRef.current = enabled
      try {
        const nextKey = `currentChatThreadId:${enabled ? 'god' : 'normal'}`
        const stored = localStorage.getItem(getUserScopedKey(nextKey))
        setCurrentThreadIdState(stored || null)
      } catch {
        setCurrentThreadIdState(null)
      }
      return enabled
    })
  }, [])

  // Defensive: keep ref aligned with state in case anything else flips it.
  useEffect(() => {
    godModeRef.current = godMode
  }, [godMode])
  const [panelViewportFullscreen, setPanelViewportFullscreenState] = useState(isInitialChatRoute)
  const panelViewportFullscreenRef = useRef(isInitialChatRoute)
  const [appSidebarOverlayOpen, setAppSidebarOverlayOpen] = useState(false)
  const [isOnboarding, setIsOnboarding] = useState(false)
  const [completingOnboarding, setCompletingOnboarding] = useState(false)
  const previousNonChatPathRef = useRef<string>(CHAT_DIRECT_EXIT_PATH)
  const urlChangeSourceRef = useRef<'fullscreen-toggle' | 'tool-navigation' | 'user-navigation' | null>(null)
  /**
   * Tracks whether WE pushed the current /chat entry via the fullscreen toggle.
   * Set true when setPanelViewportFullscreen(true) does a router.push('/chat').
   * Cleared when we leave /chat (regardless of how).
   * Used so exit-fullscreen can choose between router.back() (pop our entry)
   * vs router.replace(previousNonChatPath) (direct visit / no entry to pop).
   */
  const enteredChatViaToggleRef = useRef(false)

  const appRailCollapserRef = useRef<(() => void) | null>(null)
  const registerAppRailCollapser = useCallback((fn: (() => void) | null) => {
    appRailCollapserRef.current = fn
  }, [])

  const appRailTogglerRef = useRef<(() => void) | null>(null)
  const registerAppRailToggler = useCallback((fn: (() => void) | null) => {
    appRailTogglerRef.current = fn
  }, [])

  const toggleAppSidebar = useCallback(() => {
    appRailTogglerRef.current?.()
  }, [])

  const [appSidebarRailMode, setAppSidebarRailMode] = useState<'expanded' | 'collapsed' | null>(null)
  const registerAppSidebarRailMode = useCallback((mode: 'expanded' | 'collapsed' | null) => {
    setAppSidebarRailMode(mode)
  }, [])

  const collapseAppSidebar = useCallback(() => {
    appRailCollapserRef.current?.()
  }, [])

  const setPanelViewportFullscreen = useCallback((open: boolean) => {
    if (open) {
      setChatOpen(true)
      try {
        localStorage.setItem(LS_CHAT_WAS_OPEN, 'true')
        document.cookie = `${COOKIE_CHAT_WAS_OPEN}=true; path=/; max-age=31536000; samesite=lax`
      } catch {
        // ignore
      }
    }
    panelViewportFullscreenRef.current = open
    setPanelViewportFullscreenState(prev => (prev === open ? prev : open))
    if (!open) setAppSidebarOverlayOpen(false)

    // No-op cases:
    //  - Entering fullscreen and already on /chat
    //  - Exiting fullscreen and not on /chat (e.g. tool-navigated away while fullscreen).
    //    URL is already where the user is; exit-fullscreen just means "stop maximizing,"
    //    not "go back." Don't touch history here.
    if ((open && pathname === '/chat') || (!open && pathname !== '/chat')) {
      return
    }

    urlChangeSourceRef.current = 'fullscreen-toggle'
    startTransition(() => {
      if (open) {
        // PUSH a new history entry, not replace. This way exit-fullscreen can
        // router.back() and land cleanly on whatever the user came from.
        // (replace would clobber that entry, sending the user back two pages.)
        //
        // NOTE: we previously tried `window.history.pushState` here to keep the
        // underlying page mounted on back-nav (PR #444). It broke the post-signup
        // /chat flow — middleware redirects post-signup users to /chat via a real
        // navigation, and the pushState path left the seed/onboarding state
        // inconsistent on direct visits. Keep real App Router navigation here;
        // the @chat catch-all route closes the parallel slot on non-chat URLs.
        enteredChatViaToggleRef.current = true
        router.push('/chat', { scroll: false })
        return
      }

      // Exiting from /chat. If WE pushed the /chat entry via the fullscreen
      // toggle, router.back() pops it cleanly. Otherwise (direct visit, deep
      // link, browser-forward into /chat) replace to the last known
      // non-/chat path — we never push when entering via direct visit, so
      // there's nothing to pop and history.length is unreliable.
      if (enteredChatViaToggleRef.current) {
        enteredChatViaToggleRef.current = false
        router.back()
        return
      }

      const fallback = previousNonChatPathRef.current === '/'
        ? CHAT_DIRECT_EXIT_PATH
        : previousNonChatPathRef.current || CHAT_DIRECT_EXIT_PATH
      router.replace(fallback, { scroll: false })
    })
  }, [pathname, router])

  useEffect(() => {
    const isChatRoute = pathname === '/chat'

    if (!isChatRoute) {
      previousNonChatPathRef.current = pathname
      // Any time we leave /chat (back button, forward, tool-nav, etc.) the
      // "we pushed this entry" tracking is no longer relevant for the
      // current /chat session.
      enteredChatViaToggleRef.current = false
    }

    if (urlChangeSourceRef.current === 'tool-navigation') {
      urlChangeSourceRef.current = null
      return
    }

    if (isChatRoute) setChatOpen(true)

    if (panelViewportFullscreenRef.current === isChatRoute) {
      if (!isChatRoute) setAppSidebarOverlayOpen(false)
      urlChangeSourceRef.current = null
      return
    }

    panelViewportFullscreenRef.current = isChatRoute
    setPanelViewportFullscreenState(isChatRoute)
    if (!isChatRoute) setAppSidebarOverlayOpen(false)
    urlChangeSourceRef.current = null
  }, [pathname]) // eslint-disable-line react-hooks/exhaustive-deps -- pathname is the URL->state trigger; state reads must not loop back into URL writes.

  const flagUrlChangeSource = useCallback((source: 'tool-navigation') => {
    urlChangeSourceRef.current = source
  }, [])

  useEffect(() => {
    if (!chatOpen) {
      if (pathname === '/chat') return
      setPanelViewportFullscreen(false)
      setAppSidebarOverlayOpen(false)
    }
  }, [chatOpen, pathname, setPanelViewportFullscreen])

  useEffect(() => {
    let cancelled = false

    async function detectOnboarding() {
      const supabase = createClient()
      const { data: { user } } = await supabase.auth.getUser()
      if (!cancelled) setIsOnboarding(user ? !user.user_metadata?.has_onboarded : false)
    }

    detectOnboarding().catch(() => {
      if (!cancelled) setIsOnboarding(false)
    })

    return () => {
      cancelled = true
    }
  }, [seedMessage, completingOnboarding, pathname])

  const toggleAppSidebarOverlay = useCallback(() => {
    setAppSidebarOverlayOpen(prev => !prev)
  }, [])

  const clearAutoSendSeed = useCallback(() => setAutoSendSeed(false), [])

  // Restore thread and open state before first paint (client only).
  // currentChatThreadId is scoped by user AND by chat mode ('normal' vs 'god')
  // so toggling god mode swaps to the dedicated admin conversation without
  // mixing histories.
  useLayoutEffect(() => {
    const storedThreadId = localStorage.getItem(getUserScopedKey('currentChatThreadId:normal'))
    if (storedThreadId) setCurrentThreadIdState(storedThreadId)
    // Clean up legacy un-scoped keys to prevent cross-user/cross-mode leakage
    localStorage.removeItem('currentChatThreadId')
    localStorage.removeItem(getUserScopedKey('currentChatThreadId'))

    if (!initialChatWasOpen && pathname !== '/chat') {
      const wasOpen = localStorage.getItem(LS_CHAT_WAS_OPEN) === 'true'
      setChatOpen(wasOpen)
    }
  }, [initialChatWasOpen, pathname])

  const setCurrentThreadId = useCallback((id: string | null) => {
    setCurrentThreadIdState(id)
    // Persist under the active mode key. ChatInterface unmounts/remounts on
    // mode change so we don't need to read godMode from outside this closure.
    const modeKey = `currentChatThreadId:${godModeRef.current ? 'god' : 'normal'}`
    const key = getUserScopedKey(modeKey)
    if (id) {
      localStorage.setItem(key, id)
    } else {
      localStorage.removeItem(key)
    }
  }, [])

  const addPendingFieldContext = useCallback((ctx: PendingFieldContext) => {
    setPendingFieldContexts(prev => {
      // Replace if same field already queued
      const exists = prev.findIndex(p => p.field === ctx.field)
      if (exists >= 0) {
        const updated = [...prev]
        updated[exists] = ctx
        return updated
      }
      return [...prev, ctx]
    })
    // Also set single for backwards-compat
    setPendingFieldContext(ctx)
  }, [])

  const openChat = useCallback((intent?: FlowIntent, context?: BrandChatContext) => {
    localStorage.setItem(LS_CHAT_WAS_OPEN, 'true')
    document.cookie = `${COOKIE_CHAT_WAS_OPEN}=true; path=/; max-age=31536000; samesite=lax`
    const i = intent ?? null
    setFlowIntent(i)
    if (context?.seedMessage) {
      setPendingFieldContext(null)
      setPendingFieldContexts([])
      setSeedMessage(context.seedMessage)
      setAutoSendSeed(Boolean(context.autoSendSeed))
    } else if (i === 'brand' && context?.field) {
      const ctx = { field: context.field, currentValue: context.currentValue || '' }
      setPendingFieldContext(ctx)
      setPendingFieldContexts(prev => {
        const exists = prev.findIndex(p => p.field === context.field)
        if (exists >= 0) {
          const updated = [...prev]
          updated[exists] = ctx
          return updated
        }
        return [...prev, ctx]
      })
      setSeedMessage(null)
      setAutoSendSeed(false)
    } else {
      setPendingFieldContext(null)
      setPendingFieldContexts([])
      setSeedMessage(i ? SEED_MESSAGES[i] ?? null : null)
      setAutoSendSeed(false)
    }
    setChatOpen(true)
    queueMicrotask(() => { appRailCollapserRef.current?.() })
  }, [])

  const closeChat = useCallback(() => {
    localStorage.setItem(LS_CHAT_WAS_OPEN, 'false')
    document.cookie = `${COOKIE_CHAT_WAS_OPEN}=false; path=/; max-age=31536000; samesite=lax`
    setChatOpen(false)
    setFlowIntent(null)
    setSeedMessage(null)
    setAutoSendSeed(false)
    setPendingFieldContext(null)
    setPendingFieldContexts([])
    setPendingEventContext(null)
    setPendingCommandFormContext(null)
    setPendingScrollToMessageId(null)
    if (panelViewportFullscreen) {
      setPanelViewportFullscreen(false)
    }
  }, [panelViewportFullscreen, setPanelViewportFullscreen])

  const toggleChat = useCallback(() => {
    setChatOpen(v => {
      const next = !v
      localStorage.setItem(LS_CHAT_WAS_OPEN, next ? 'true' : 'false')
      document.cookie = `${COOKIE_CHAT_WAS_OPEN}=${next ? 'true' : 'false'}; path=/; max-age=31536000; samesite=lax`
      if (v) {
        setFlowIntent(null)
        setSeedMessage(null)
        setAutoSendSeed(false)
        setPendingFieldContext(null)
        setPendingCommandFormContext(null)
        setPendingEventContext(null)
        queueMicrotask(() => { setPendingScrollToMessageId(null) })
      }
      if (next) {
        queueMicrotask(() => { appRailCollapserRef.current?.() })
      }
      return next
    })
  }, [])

  const clearSeedMessage = useCallback(() => setSeedMessage(null), [])

  return (
    <ChatContext.Provider value={{
      chatOpen,
      flowIntent,
      seedMessage,
      pendingFieldContext,
      setPendingFieldContext,
      addPendingFieldContext,
      pendingFieldContexts,
      setPendingFieldContexts,
      pendingEventContext,
      setPendingEventContext,
      pendingSonobiQuery,
      setPendingSonobiQuery,
      pendingCommandFormContext,
      setPendingCommandFormContext,
      proposedCommandFormPatches,
      setProposedCommandFormPatches,
      openChat,
      closeChat,
      toggleChat,
      clearSeedMessage,
      clearAutoSendSeed,
      autoSendSeed,
      currentThreadId,
      setCurrentThreadId,
      godMode,
      setGodMode,
      droppedFile,
      setDroppedFile,
      registerAppRailCollapser,
      appSidebarRailMode,
      registerAppSidebarRailMode,
      collapseAppSidebar,
      registerAppRailToggler,
      toggleAppSidebar,
      pendingScrollToMessageId,
      setPendingScrollToMessageId,
      panelViewportFullscreen,
      setPanelViewportFullscreen,
      flagUrlChangeSource,
      isOnboarding,
      completingOnboarding,
      setCompletingOnboarding,
      appSidebarOverlayOpen,
      setAppSidebarOverlayOpen,
      toggleAppSidebarOverlay,
    }}>
      {children}
    </ChatContext.Provider>
  )
}

export function useChat() {
  const ctx = useContext(ChatContext)
  if (!ctx) throw new Error('useChat must be used within ChatProvider')
  return ctx
}
